import { useEffect, useMemo, useState } from 'react';
import { Badge, Button, Modal, Table } from 'react-bootstrap';
import Select from 'react-select';
import { useDispatch, useSelector } from 'react-redux';

import {
    buscarAreasConfiguracion,
    buscarOrganizadoresConfiguracion,
    buscarUbicacionesConfiguracion
} from '../../../actions/configuracion';

const formatNumber = (value) => {
    const v = Number(value || 0);
    return new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(v);
};

const toOption = (item) => ({ value: item?._id || '', label: item?.nombre || 'Sin definir' });

const componentesVacios = (componentes) => (componentes || []).reduce((acc, c) => {
    acc[c.productoId] = { area: null, organizador: null, ubicacion: null };
    return acc;
}, {});

const filaVacia = (cantidad, componentes) => ({
    rowId: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    cantidad,
    componentes: componentesVacios(componentes)
});

/* Devolver un paquete: la cantidad sigue siendo a nivel paquete (número de paquetes devueltos
   por lote), pero cada producto de la receta tiene su propia ubicación destino — ya no todo el
   paquete va a un solo lugar. El backend reparte la cantidad de cada producto según su proporción
   en la receta y mueve cada uno a la ubicación que se le indique aquí. */
const DevolverPaqueteVentaModal = ({ show, onHide, item, onConfirm }) => {
    const dispatch = useDispatch();

    const { areas } = useSelector(state => state.configuracion);

    const disponible = Number(item?.cantidad || 0);
    const componentes = useMemo(() => (Array.isArray(item?.componentes) ? item.componentes : []), [item]);

    const [filas, setFilas] = useState([]);
    const [organizadoresPorCelda, setOrganizadoresPorCelda] = useState({});
    const [ubicacionesPorCelda, setUbicacionesPorCelda] = useState({});

    const opcionesArea = useMemo(() => (areas?.listado || []).map(toOption), [areas]);

    useEffect(() => {
        if (!show) {
            setFilas([]);
            setOrganizadoresPorCelda({});
            setUbicacionesPorCelda({});
            return;
        }

        dispatch(buscarAreasConfiguracion());
        setFilas([filaVacia(disponible, componentes)]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show, dispatch, disponible]);

    const asignado = filas.reduce((acc, f) => acc + Number(f.cantidad || 0), 0);
    const faltaUbicacion = filas.some((f) => {
        if (Number(f.cantidad || 0) <= 0) return false;
        return componentes.some((c) => !f.componentes[c.productoId]?.area?.value);
    });
    const puedeConfirmar = asignado > 0 && asignado <= disponible && !faltaUbicacion && componentes.length > 0;

    const actualizarFila = (rowId, cambios) => {
        setFilas((prev) => prev.map((f) => (f.rowId === rowId ? { ...f, ...cambios } : f)));
    };

    const actualizarComponenteFila = (rowId, productoId, cambios) => {
        setFilas((prev) => prev.map((f) => {
            if (f.rowId !== rowId) return f;
            return {
                ...f,
                componentes: {
                    ...f.componentes,
                    [productoId]: { ...(f.componentes[productoId] || {}), ...cambios }
                }
            };
        }));
    };

    const handleAgregarFila = () => {
        setFilas((prev) => [...prev, filaVacia(0, componentes)]);
    };

    const handleEliminarFila = (rowId) => {
        setFilas((prev) => prev.filter((f) => f.rowId !== rowId));
    };

    const handleChangeCantidad = (rowId, value) => {
        const v = value === '' ? '' : Math.max(0, Number(value));
        actualizarFila(rowId, { cantidad: v });
    };

    const handleSelectArea = async (rowId, productoId, opt) => {
        const celda = `${rowId}__${productoId}`;
        actualizarComponenteFila(rowId, productoId, { area: opt || null, organizador: null, ubicacion: null });
        setOrganizadoresPorCelda((prev) => ({ ...prev, [celda]: [] }));
        setUbicacionesPorCelda((prev) => ({ ...prev, [celda]: [] }));

        if (!opt?.value) return;

        const res = await dispatch(buscarOrganizadoresConfiguracion(opt.value));
        setOrganizadoresPorCelda((prev) => ({ ...prev, [celda]: res?.organizadores || [] }));
    };

    const handleSelectOrganizador = async (rowId, productoId, opt) => {
        const celda = `${rowId}__${productoId}`;
        actualizarComponenteFila(rowId, productoId, { organizador: opt || null, ubicacion: null });
        setUbicacionesPorCelda((prev) => ({ ...prev, [celda]: [] }));

        if (!opt?.value) return;

        const res = await dispatch(buscarUbicacionesConfiguracion(opt.value));
        setUbicacionesPorCelda((prev) => ({ ...prev, [celda]: res?.ubicaciones || [] }));
    };

    const handleSelectUbicacion = (rowId, productoId, opt) => {
        actualizarComponenteFila(rowId, productoId, { ubicacion: opt || null });
    };

    const handleConfirmar = () => {
        if (!puedeConfirmar) return;

        const destinos = filas
            .filter((f) => Number(f.cantidad || 0) > 0)
            .map((f) => ({
                cantidad: Number(f.cantidad),
                componentes: componentes.map((c) => {
                    const sel = f.componentes[c.productoId] || {};
                    return {
                        productoId: c.productoId,
                        area: sel.area ? { _id: sel.area.value, nombre: sel.area.label } : null,
                        organizador: sel.organizador ? { _id: sel.organizador.value, nombre: sel.organizador.label } : null,
                        ubicacion: sel.ubicacion ? { _id: sel.ubicacion.value, nombre: sel.ubicacion.label } : null
                    };
                })
            }));

        onConfirm(destinos);
    };

    return (
        <Modal show={show} onHide={onHide} size="xl" backdrop="static" keyboard={false}>
            <Modal.Header closeButton>
                <Modal.Title>Devolver paquete</Modal.Title>
            </Modal.Header>

            <Modal.Body>
                <div className="mb-3">
                    <div className="fw-semibold">{item?.paqueteNombre}</div>
                    <div className="text-muted" style={{ fontSize: 13 }}>
                        Cantidad de paquetes disponible para devolver: {formatNumber(disponible)}
                    </div>
                </div>

                <div className="text-muted mb-3" style={{ fontSize: 13 }}>
                    Indica cuántos paquetes devuelves en cada lote y a qué ubicación regresa cada producto del paquete.
                    Puedes dividir la cantidad de paquetes en varios lotes si necesitas repartirlos de forma distinta.
                </div>

                {filas.map((f, idx) => {
                    const cantidadFila = Number(f.cantidad || 0);

                    return (
                        <div key={f.rowId} className="border rounded p-3 mb-3">
                            <div className="d-flex align-items-center justify-content-between mb-2">
                                <div className="d-flex align-items-center gap-2">
                                    <div className="text-muted" style={{ fontSize: 12 }}>Lote {idx + 1} · Paquetes</div>
                                    <input
                                        type="number"
                                        className="form-control form-control-sm"
                                        style={{ width: 90 }}
                                        value={f.cantidad}
                                        min={0}
                                        onChange={(e) => handleChangeCantidad(f.rowId, e.target.value)}
                                    />
                                </div>
                                {filas.length > 1 && (
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-link px-1"
                                        title="Eliminar lote"
                                        onClick={() => handleEliminarFila(f.rowId)}
                                    >
                                        <i className="fas fa-trash text-danger" />
                                    </button>
                                )}
                            </div>

                            <div className="table-responsive">
                                <Table bordered hover size="sm" className="mb-0">
                                    <thead>
                                        <tr>
                                            <th>Producto</th>
                                            <th className="text-center" style={{ width: 100 }}>Cantidad</th>
                                            <th>Ubicación destino</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {componentes.map((c) => {
                                            const conversion = Number(c?.conversion || 1) || 1;
                                            const cantidadComponente = (Number(c?.cantidadPorPaquete || 0) * cantidadFila) / conversion;
                                            const celda = `${f.rowId}__${c.productoId}`;
                                            const sel = f.componentes[c.productoId] || {};

                                            return (
                                                <tr key={c.productoId}>
                                                    <td className="align-middle">{c.productoNombre}</td>
                                                    <td className="text-center align-middle">{formatNumber(cantidadComponente)}</td>
                                                    <td>
                                                        <div className="d-flex align-items-center gap-2">
                                                            <div style={{ minWidth: 130, maxWidth: 160 }}>
                                                                <Select
                                                                    options={opcionesArea}
                                                                    value={sel.area}
                                                                    onChange={(opt) => handleSelectArea(f.rowId, c.productoId, opt)}
                                                                    isClearable
                                                                    placeholder="Área"
                                                                    noOptionsMessage={() => 'Sin resultados'}
                                                                    menuPortalTarget={document.body}
                                                                    styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                                                                />
                                                            </div>
                                                            <div style={{ minWidth: 130, maxWidth: 160 }}>
                                                                <Select
                                                                    options={(organizadoresPorCelda[celda] || []).map(toOption)}
                                                                    value={sel.organizador}
                                                                    onChange={(opt) => handleSelectOrganizador(f.rowId, c.productoId, opt)}
                                                                    isClearable
                                                                    isDisabled={!sel.area}
                                                                    placeholder="Organizador"
                                                                    noOptionsMessage={() => 'Sin resultados'}
                                                                    menuPortalTarget={document.body}
                                                                    styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                                                                />
                                                            </div>
                                                            <div style={{ minWidth: 130, maxWidth: 160 }}>
                                                                <Select
                                                                    options={(ubicacionesPorCelda[celda] || []).map(toOption)}
                                                                    value={sel.ubicacion}
                                                                    onChange={(opt) => handleSelectUbicacion(f.rowId, c.productoId, opt)}
                                                                    isClearable
                                                                    isDisabled={!sel.organizador}
                                                                    placeholder="Ubicación"
                                                                    noOptionsMessage={() => 'Sin resultados'}
                                                                    menuPortalTarget={document.body}
                                                                    styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                                                                />
                                                            </div>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </Table>
                            </div>
                        </div>
                    );
                })}

                <div className="d-flex align-items-center justify-content-between mt-2">
                    <button type="button" className="btn btn-sm btn-outline-primary" onClick={handleAgregarFila}>
                        <i className="feather icon-plus" /> Agregar otro lote
                    </button>
                    <div className={puedeConfirmar ? 'text-success' : 'text-muted'} style={{ fontSize: 12 }}>
                        Asignado: {formatNumber(asignado)} / {formatNumber(disponible)}
                    </div>
                </div>

                {!puedeConfirmar && (
                    <div className="mt-3">
                        <Badge bg="danger">Asigna una ubicación a cada producto del paquete en los lotes con cantidad, sin exceder lo disponible</Badge>
                    </div>
                )}
            </Modal.Body>

            <Modal.Footer>
                <Button variant="secondary" onClick={onHide}>
                    Cancelar
                </Button>
                <Button variant="success" onClick={handleConfirmar} disabled={!puedeConfirmar}>
                    Confirmar devolución
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default DevolverPaqueteVentaModal;
